import _ from "lodash";
import * as moment from "moment";
import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter, Link } from "react-router-dom";
import * as redux from "redux";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import { StacyKiff } from "../../../constants/contacts";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import Settings from "../../../settings";
import { AppState } from "../../../store";
import { Utility } from "../../../utils";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultStatePropsMap(state, ownProps),
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
});

type OwnProps = RouteComponentProps<{
    guid: string;
}> & {
};

type ConnectedState = Comp.Base.DefaultConnectedState & {
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
};

interface OwnState {
    abstract?: M.Abstract.Abstract;
    loadingAbstract: boolean;
    loadingType: boolean;
    type?: M.Abstract.AbstractType;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class ConfirmationComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    public constructor(props: any) {
        super(props);
        this.state = {
            loadingAbstract: true,
            loadingType: true,
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        this.props.updateChildOfPath(Paths.User.Abstracts.UserAbstracts);
        const guid = this.props.match.params.guid;

        if (!Utility.isGuid(guid)) {
            this.props.notifyError("Invalid abstract identifier in URL");
            this.props.replace(Paths.User.Abstracts.UserAbstracts);
            return;
        }

        if (Utility.isGuid(guid)) {
            const userId = this.props.tokenData.userId || 0;
            const hasAccess = await M.Abstract.userHasAccessToAbstract(this.props.api, userId, guid);
            if (!hasAccess) {
                this.props.notifyError("You do not have access to the requested abstract.");
                this.props.replace(Paths.User.Abstracts.UserAbstracts);
                return;
            }
        }

        this.loadData();
    }

    public componentWillUnmount() {
        this._isMounted = false;
        this.props.updateChildOfPath();
    }

    public render() {
        const abstractTitle = this.state.abstract ? this.state.abstract.title : "Untitled";
        const abstractType = this.state.type ? this.state.type.name : "Unknown type";
        const deadline = this.state.type ? moment(Utility.utcToLocal(this.state.type.submitDeadline)).add(1, "day").format("MMMM D, YYYY") : "";
        const eventId = this.state.abstract ? this.state.abstract.eventId : 0;
        const primaryAuthor = this.state.abstract ? _.find(this.state.abstract.authors, (x) => x.isPrimary) : undefined;
        const primaryAuthorEmail = primaryAuthor ? primaryAuthor.email : "";
        const coAuthors = this.state.abstract ? this.state.abstract.authors.filter((x) => !x.isPrimary) : [];
        const contact = StacyKiff;

        const coAuthorsDisplay = coAuthors.length
            ? <Comp.Generic.WrapperLess>
                <p>An automated email has been sent to your co-author(s) requesting update of their financial disclosure:</p>
                <ul>
                    {coAuthors.map((a, i) => <li key={i}>{M.Generic.PersonHelper.getFullName(a)}: <Comp.Generic.EmailLink address={a.email} /></li>)}
                </ul>
            </Comp.Generic.WrapperLess>
            : null;

        return <Comp.Page.PublicPage additionalClasses="full" pageTitle="Abstract submission confirmation" style={{ position: "relative" }}>
            <Comp.Generic.LoadingSpinner show={this.state.loadingAbstract || this.state.loadingType} />

            <Comp.Layout.ContentPrimary>

                {this.getErrorAlert()}

                <h1>Thank you for your abstract submission!</h1>

                <p>
                    Thank you for submitting your <strong>{abstractType}</strong> abstract
                    entitled <strong dangerouslySetInnerHTML={{ __html: abstractTitle}} /> for consideration
                    in the scientific program of the American Society of Retina Specialists Annual Meeting. A
                    confirmation email has been sent to <Comp.Generic.EmailLink address={primaryAuthorEmail} />.
                </p>

                <h3><strong>Financial disclosures required by {deadline}</strong></h3>

                <p>
                    Financial disclosures are required for ALL authors and co-authors. Abstracts will not be reviewed
                    until financial disclosures have been submitted for all authors. An abstract will not be considered
                    complete and eligible for consideration until all financial disclosures are on record.
                </p>

                <h3>
                    <strong><Link to={Paths.User.FinancialDisclosureUpdate}>Update your financial disclosure &raquo;</Link></strong>
                </h3>

                {coAuthorsDisplay}

                <hr />

                <h3>Next steps</h3>

                <ul>
                    <li><Link to={Paths.User.Abstracts.UserAbstracts}>View your abstract submission status at any time &raquo;</Link></li>
                    <li><Link to={Paths.AbstractsComposeNew(eventId)}>Submit another abstract &raquo;</Link></li>
                    <li><a href={Settings.AsrsLegacyUrl + "/annual-meeting"}>View Annual Meeting information &raquo;</a></li>
                </ul>

                <hr />

                <h3>Questions?</h3>

                <p>
                    <strong>Contact</strong>
                    <br />{contact.name}
                    <br />{contact.title}
                    <br /><Comp.Generic.EmailLink address={contact.address} />
                    <br />{contact.phone}
                </p>
            </Comp.Layout.ContentPrimary>

            <Comp.Layout.ContentSecondary>

            </Comp.Layout.ContentSecondary>
            <div className="clear"></div>
        </Comp.Page.PublicPage>;
    }

    private loadData() {
        const guid = this.props.match.params.guid;
        if (Utility.isGuid(guid)) {
            this.loadAbstract(guid);
            this.loadAbstractType(guid);
        }
    }

    private async loadAbstract(guid: string) {
        const result = await this.props.api.get(Api.endpoints.abstract.abstract.withUrlParams({ guid }));
        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            if (M.Abstract.AbstractStatuses.isUnsubmitted(result.data)) {
                this.props.goto(Paths.AbstractsCompose(result.data.eventId, "review", result.data.guid));
                return;
            }

            this.setState({ ...this.state, abstract: result.data, loadingAbstract: false });
        }
        else {
            this.setState({ ...this.state, loadingAbstract: false }, () => {
                this.setErrors(M.Result.Result.errorsOrMessage(result), "Failed to load abstract");
            });
        }
    }

    private async loadAbstractType(guid: string) {
        const result = await this.props.api.get(Api.endpoints.abstract.typeByAbstractGuid.withUrlParams({ guid }));
        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            this.setState({ ...this.state, type: result.data, loadingType: false });
        }
        else {
            this.setState({ ...this.state, loadingType: false }, () => {
                this.setErrors(M.Result.Result.errorsOrMessage(result), "Failed to load abstract type");
            });
        }
    }
}

export const Confirmation: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(ConfirmationComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/abstracts/composer/Confirmation.tsx